/** Where the move grip can sit relative to the table it belongs to. */
export type GripSide = 'right' | 'left' | 'top' | 'bottom' | 'inside';

/** Just enough of a `DOMRect` to place a handle against one. */
export interface GripRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
}

/**
 * Which side of a table its move grip goes on, given the page around it.
 *
 * The grip is drawn OUTSIDE the table, clear of the corner squares and the edge
 * strips, so that "move" is never a near-miss for "resize" -- and outside is
 * exactly what the page sheet clips. A grip in the clipped band is not merely
 * ugly, it is unreachable, and it is the only way to move a table: lose it and
 * the author is left with a table they cannot move at all. So the side is
 * chosen from the room that is actually on the page.
 *
 * **The order is the point.** Right first, because that is where an author has
 * already learned to look. Then left. Only then above or below -- a table wider
 * than the page has no horizontal room on EITHER side, which is the case that
 * has no answer in the left/right pair and the reason the vertical ones exist.
 *
 * `inside` is the last resort, for a table larger than the page in both axes:
 * the grip moves in over the table's own top-left corner, where it overlaps a
 * cell. Overlapping a cell is a real cost, and it is still the right trade --
 * the alternative is an element that cannot be moved, and any part of the grip
 * being reachable beats all of it being correct and invisible.
 *
 * A page with no width has not laid out yet. That is not a measurement, so it
 * yields the default rather than a guess; the caller measures again when a
 * layout arrives.
 */
export function chooseGripSide(table: GripRect, page: GripRect, clearance: number): GripSide {
  if (page.width <= 0) return 'right';
  if (page.right - table.right >= clearance) return 'right';
  if (table.left - page.left >= clearance) return 'left';
  if (table.top - page.top >= clearance) return 'top';
  if (page.bottom - table.bottom >= clearance) return 'bottom';
  return 'inside';
}

/**
 * Where each side puts the grip, as the classes that place it.
 *
 * A table rather than a chain of ternaries in the markup: five placements is
 * where "which one am I looking at" stops being obvious, and each is a pair of
 * axis rules that have to agree with each other.
 */
export const GRIP_POSITION: Record<GripSide, string> = {
  right: 'top-1/2 -right-9 -translate-y-1/2',
  left: 'top-1/2 -left-9 -translate-y-1/2',
  top: '-top-9 left-1/2 -translate-x-1/2',
  bottom: '-bottom-9 left-1/2 -translate-x-1/2',
  // Inside the table, not straddling its edge: every other placement hangs off
  // the edge it names, and this one exists precisely because there is no room
  // outside any edge to hang off.
  inside: 'top-1 left-1',
};

/** Room the move grip needs beside the table: its own 28px, plus the gap that
 *  keeps it off the corner squares. */
export const GRIP_CLEARANCE = 40;

/**
 * The four whole-table resize handles, each pinned to its corner and carrying
 * the diagonal cursor that says which way it grows.
 *
 * A list rather than four near-identical blocks: they differ by three values and
 * nothing else, and four copies is how three of them come to be updated and the
 * fourth forgotten.
 */
export const TABLE_CORNERS = [
  {dir: 'nw', className: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2', cursor: 'nwse-resize'},
  {dir: 'ne', className: 'top-0 right-0 translate-x-1/2 -translate-y-1/2', cursor: 'nesw-resize'},
  {dir: 'sw', className: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2', cursor: 'nesw-resize'},
  {dir: 'se', className: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2', cursor: 'nwse-resize'},
] as const;

/** The four corners, named. Derived from the table above rather than written out
 *  again, so a corner cannot exist in one and not the other. */
export type TableCorner = (typeof TABLE_CORNERS)[number]['dir'];
